/* WS4 (Free Design Package) — admin six-module UI (B7a).

   Reference: 547.html (main workspace) and 5410 - HTML.html (Feedback Review).
   All six modules are full-width vertical (per §8.1 / §14).  This is a
   minimal admin foundation; the Phase C client Portal WS4 pages are out
   of scope.
*/
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { readErrorDetail } from "../../lib/errors";
import { usePageMeta } from "../../components/Layout";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api`;

function ModuleCard({ n, title, children, testid }) {
    return (
        <section
            data-testid={testid}
            className="rounded-lg border border-[#E5E2DC] bg-white shadow-sm">
            <header className="border-b border-[#E5E2DC] px-4 py-3">
                <h3 className="font-manrope text-[13px] font-semibold uppercase tracking-wide text-[#1D1D1B]">
                    {n}. {title}
                </h3>
            </header>
            <div className="px-4 py-4 text-[14px] text-[#1D1D1B]">
                {children}
            </div>
        </section>
    );
}

function Empty({ label }) {
    return <div className="text-[13px] text-[#888]">{label}</div>;
}

function Pill({ tone = "neutral", children, testid }) {
    const map = {
        neutral: "bg-[#F5F3EE] text-[#1D1D1B]",
        good: "bg-[#E4EFE4] text-[#345E34]",
        warn: "bg-[#FBEEDF] text-[#7A4A22]",
        active: "bg-[#A8543F] text-white",
    };
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${map[tone]}`}>
            {children}
        </span>
    );
}

// §Phase 3 — WS4 Portal admin panel.  Four states keyed on the shape
// of the loaded `portal` row:
//   null          → "Create Portal Account" primary action.
//   Active        → Rotate / Revoke actions.
//   Revoked       → Reactivate (mints a brand-new password).
// Password plaintext is returned exactly once by the backend; the
// one-shot credential modal is the ONLY place it is ever shown.
function _uuid4() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function PortalPanel({ pid, portal, reload, clientName }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [confirmAction, setConfirmAction] = useState(null); // 'create'|'rotate'|'revoke'|'reactivate'
    const [revokeTyped, setRevokeTyped] = useState("");
    const [revokeReason, setRevokeReason] = useState("");
    const [issuedCred, setIssuedCred] = useState(null); // {public_project_id, password_plaintext}
    const [ackCopied, setAckCopied] = useState(false);
    const [copied, setCopied] = useState({ id: false, pw: false });

    const state = portal
        ? (portal.access_status === "Active" ? "active" : "revoked")
        : "none";

    async function post(path, body) {
        setBusy(true); setErr("");
        try {
            const r = await axios.post(`${API}/admin/projects/${pid}/ws4/portal/${path}`, body);
            return r.data?.workspace;
        } catch (ex) {
            setErr(readErrorDetail(ex) || "Request failed");
            return null;
        } finally { setBusy(false); }
    }

    async function doCreate() {
        const ws = await post("create", { create_op_id: _uuid4() });
        if (ws?.issued_credential?.password_plaintext) {
            setIssuedCred(ws.issued_credential);
        }
        setConfirmAction(null);
        reload();
    }

    async function doRotate() {
        const ws = await post("rotate-password", { create_op_id: _uuid4() });
        if (ws?.issued_credential?.password_plaintext) {
            setIssuedCred(ws.issued_credential);
        }
        setConfirmAction(null);
        reload();
    }

    async function doRevoke() {
        const reason = revokeReason.trim() || "Admin revocation";
        const ws = await post("revoke", { create_op_id: _uuid4(), reason });
        if (ws) {
            setConfirmAction(null);
            setRevokeTyped(""); setRevokeReason("");
            reload();
        }
    }

    async function doReactivate() {
        const ws = await post("reactivate", { create_op_id: _uuid4() });
        if (ws?.issued_credential?.password_plaintext) {
            setIssuedCred(ws.issued_credential);
        }
        setConfirmAction(null);
        reload();
    }

    function copy(key, val) {
        if (!navigator?.clipboard) return;
        navigator.clipboard.writeText(val);
        setCopied(c => ({ ...c, [key]: true }));
        setTimeout(() => setCopied(c => ({ ...c, [key]: false })), 2000);
    }

    const revokeNameOk = revokeTyped.trim() && clientName
        && revokeTyped.trim().toLowerCase() === clientName.trim().toLowerCase();

    return (
        <div data-testid="ws4-portal-panel">
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                    <strong>Client Portal:</strong>{" "}
                    {state === "none" && (
                        <span className="text-[#888]" data-testid="ws4-portal-none">
                            Not created — a Client Portal is normally activated before the first
                            client-facing Design Issue is formally issued.
                        </span>
                    )}
                    {state === "active" && (
                        <span data-testid="ws4-portal-active">
                            <Pill tone="active" testid="ws4-portal-status">Active</Pill>{" "}
                            <code className="text-[12.5px]">{portal.public_project_id}</code>
                            {portal.password_last_rotated_at && (
                                <> · last credential {String(portal.password_last_rotated_at).slice(0, 10)}</>
                            )}
                        </span>
                    )}
                    {state === "revoked" && (
                        <span data-testid="ws4-portal-revoked">
                            <Pill tone="warn" testid="ws4-portal-status">Revoked</Pill>{" "}
                            {portal.revocation_reason && (
                                <span className="text-[12.5px] text-[#7A5148]">{portal.revocation_reason}</span>
                            )}
                            {portal.revoked_at && (
                                <> · {String(portal.revoked_at).slice(0, 10)}</>
                            )}
                        </span>
                    )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    {state === "none" && (
                        <button type="button" disabled={busy}
                            onClick={() => setConfirmAction("create")}
                            data-testid="ws4-portal-create-btn"
                            className="rounded-md bg-[#A8543F] text-white text-[12px] px-3 py-1.5 hover:opacity-90 disabled:opacity-50">
                            Create Portal Account
                        </button>
                    )}
                    {state === "active" && (<>
                        <button type="button" disabled={busy}
                            onClick={() => setConfirmAction("rotate")}
                            data-testid="ws4-portal-rotate-btn"
                            className="rounded-md border border-[#A8543F] text-[#A8543F] text-[12px] px-3 py-1.5 hover:bg-[#A8543F] hover:text-white disabled:opacity-50">
                            Rotate Password
                        </button>
                        <button type="button" disabled={busy}
                            onClick={() => setConfirmAction("revoke")}
                            data-testid="ws4-portal-revoke-btn"
                            className="rounded-md border border-[#7A2E1E] text-[#7A2E1E] text-[12px] px-3 py-1.5 hover:bg-[#7A2E1E] hover:text-white disabled:opacity-50">
                            Revoke Access
                        </button>
                    </>)}
                    {state === "revoked" && (
                        <button type="button" disabled={busy}
                            onClick={() => setConfirmAction("reactivate")}
                            data-testid="ws4-portal-reactivate-btn"
                            className="rounded-md bg-[#A8543F] text-white text-[12px] px-3 py-1.5 hover:opacity-90 disabled:opacity-50">
                            Reactivate (new password)
                        </button>
                    )}
                </div>
            </div>
            {err && (
                <div className="mt-2 text-[12.5px] text-[#8C2E1E] bg-[#F6E0DA] border border-[#E9C6BE] rounded p-2.5"
                     data-testid="ws4-portal-err">{err}</div>
            )}

            {/* Confirm — Create */}
            {confirmAction === "create" && (
                <PortalConfirmModal
                    title="Create portal account"
                    body="Create a portal account for this client? A new password will be shown once."
                    confirmLabel="Create"
                    confirmTestid="ws4-portal-create-confirm"
                    cancelTestid="ws4-portal-create-cancel"
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={doCreate}
                    busy={busy} />
            )}

            {/* Confirm — Rotate */}
            {confirmAction === "rotate" && (
                <PortalConfirmModal
                    title="Rotate password"
                    body="Generate a new password? The old one will stop working immediately."
                    confirmLabel="Rotate"
                    confirmTestid="ws4-portal-rotate-confirm"
                    cancelTestid="ws4-portal-rotate-cancel"
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={doRotate}
                    busy={busy} />
            )}

            {/* Confirm — Revoke (typed confirmation) */}
            {confirmAction === "revoke" && (
                <PortalConfirmModal
                    title="Revoke portal access"
                    body={
                        <div className="space-y-3 text-[13px]">
                            <div>Revoke client portal access? The client will be signed out of
                                any active session and the password will be invalidated.</div>
                            <div>
                                <label className="block text-[11px] uppercase tracking-wide text-[#666] font-manrope font-semibold">
                                    Type the client name to confirm{clientName ? <> (<code>{clientName}</code>)</> : null}
                                </label>
                                <input type="text" value={revokeTyped}
                                    onChange={e => setRevokeTyped(e.target.value)}
                                    data-testid="ws4-portal-revoke-typed-name"
                                    className="mt-1 w-full px-2 py-1.5 border border-[#D7D4CC] rounded text-[13px] focus:outline-none focus:border-[#A8543F]" />
                            </div>
                            <div>
                                <label className="block text-[11px] uppercase tracking-wide text-[#666] font-manrope font-semibold">
                                    Reason (optional)
                                </label>
                                <input type="text" value={revokeReason}
                                    onChange={e => setRevokeReason(e.target.value)}
                                    data-testid="ws4-portal-revoke-reason"
                                    placeholder="Admin revocation"
                                    className="mt-1 w-full px-2 py-1.5 border border-[#D7D4CC] rounded text-[13px] focus:outline-none focus:border-[#A8543F]" />
                            </div>
                        </div>
                    }
                    confirmLabel="Revoke access"
                    confirmTestid="ws4-portal-revoke-confirm"
                    cancelTestid="ws4-portal-revoke-cancel"
                    confirmDisabled={!revokeNameOk}
                    danger
                    onCancel={() => { setConfirmAction(null); setRevokeTyped(""); setRevokeReason(""); }}
                    onConfirm={doRevoke}
                    busy={busy} />
            )}

            {/* Confirm — Reactivate */}
            {confirmAction === "reactivate" && (
                <PortalConfirmModal
                    title="Reactivate portal access"
                    body="Reactivate client portal access? A new password will be shown once; the previous password is NOT restored."
                    confirmLabel="Reactivate"
                    confirmTestid="ws4-portal-reactivate-confirm"
                    cancelTestid="ws4-portal-reactivate-cancel"
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={doReactivate}
                    busy={busy} />
            )}

            {/* One-shot credential modal */}
            {issuedCred && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                     data-testid="ws4-portal-credential-modal">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 border border-[#E5E2DC]">
                        <h3 className="font-manrope text-[16px] font-semibold text-charcoal mb-1">
                            Portal credentials
                        </h3>
                        <p className="text-[12.5px] text-[#6B6B67] mb-4">
                            Share these securely with the client. The password is shown here
                            exactly once — if you lose it, you must rotate.
                        </p>
                        <div className="space-y-3">
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-[#666] font-manrope font-semibold mb-1">
                                    Portal ID
                                </div>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 px-3 py-2 bg-[#F5F3EE] border border-[#E5E2DC] rounded text-[13px] font-mono"
                                          data-testid="ws4-portal-credential-id">
                                        {issuedCred.public_project_id}
                                    </code>
                                    <button type="button"
                                        onClick={() => copy("id", issuedCred.public_project_id)}
                                        data-testid="ws4-portal-credential-copy-id"
                                        className="rounded-md border border-[#A8543F] text-[#A8543F] text-[12px] px-3 py-1.5 hover:bg-[#A8543F] hover:text-white">
                                        {copied.id ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-[#666] font-manrope font-semibold mb-1">
                                    Password
                                </div>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 px-3 py-2 bg-[#F5F3EE] border border-[#E5E2DC] rounded text-[13px] font-mono"
                                          data-testid="ws4-portal-credential-pw">
                                        {issuedCred.password_plaintext}
                                    </code>
                                    <button type="button"
                                        onClick={() => copy("pw", issuedCred.password_plaintext)}
                                        data-testid="ws4-portal-credential-copy-pw"
                                        className="rounded-md border border-[#A8543F] text-[#A8543F] text-[12px] px-3 py-1.5 hover:bg-[#A8543F] hover:text-white">
                                        {copied.pw ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <label className="mt-4 flex items-start gap-2 text-[12.5px] text-[#4a4740]">
                            <input type="checkbox" checked={ackCopied}
                                onChange={e => setAckCopied(e.target.checked)}
                                data-testid="ws4-portal-credential-ack"
                                className="mt-0.5" />
                            <span>
                                I've copied these credentials — I understand the password won't be
                                shown again.
                            </span>
                        </label>
                        <div className="mt-5 flex justify-end">
                            <button type="button" disabled={!ackCopied}
                                onClick={() => { setIssuedCred(null); setAckCopied(false); }}
                                data-testid="ws4-portal-credential-close"
                                className="rounded-md bg-[#A8543F] text-white text-[13px] px-4 py-2 disabled:opacity-50">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function PortalConfirmModal({ title, body, confirmLabel, confirmTestid,
                              cancelTestid, confirmDisabled, danger,
                              onConfirm, onCancel, busy }) {
    return (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4"
             data-testid="ws4-portal-confirm-modal">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 border border-[#E5E2DC]">
                <h3 className="font-manrope text-[15px] font-semibold text-charcoal mb-2">{title}</h3>
                <div className="text-[13px] text-[#4a4740]">{body}</div>
                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onCancel} disabled={busy}
                        data-testid={cancelTestid}
                        className="rounded-md border border-[#D7D4CC] text-[13px] px-3 py-1.5 text-[#1D1D1B] hover:bg-[#F5F3EE] disabled:opacity-50">
                        Cancel
                    </button>
                    <button type="button" onClick={onConfirm}
                        disabled={busy || confirmDisabled}
                        data-testid={confirmTestid}
                        className={`rounded-md text-white text-[13px] px-4 py-1.5 disabled:opacity-50 ${
                            danger ? "bg-[#7A2E1E]" : "bg-[#A8543F]"}`}>
                        {busy ? "Working…" : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}


/* ------------------------------------------------------------------ */
/* Compact modal shell — reused for Mark Final/Current and Package    */
/* Summary editor.  No external dep; consistent with the existing     */
/* admin surface's small dialogs.                                      */
/* ------------------------------------------------------------------ */
function ModalShell({ title, onClose, children, testid, wide = false }) {
    return (
        <div
            data-testid={testid}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div
                className={`bg-white rounded-lg shadow-lg border border-[#E5E2DC] w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}>
                <header className="border-b border-[#E5E2DC] px-4 py-3 flex items-center">
                    <h3 className="font-manrope text-[13px] font-semibold uppercase tracking-wide text-[#1D1D1B]">
                        {title}
                    </h3>
                    <button
                        type="button" onClick={onClose}
                        data-testid={`${testid}-close`}
                        className="ml-auto text-[13px] text-[#666] hover:text-[#1D1D1B]">
                        Close
                    </button>
                </header>
                <div className="px-4 py-4 text-[14px] text-[#1D1D1B]">
                    {children}
                </div>
            </div>
        </div>
    );
}

/* Mark Final/Current — deliberate contextual action per §1A.  Selects
   the exact File version being designated; if the deliverable is
   already Final/Current on a different version, a reason is required
   (backend accepts `reason`). */
function MarkFinalCurrentModal({ projectId, deliverable, onClose, onDone }) {
    const versions = deliverable?.versions || [];
    const currentFvid = deliverable?.final_current_file_version_id || null;
    const [selected, setSelected] = useState(
        currentFvid || (versions.length ? versions[versions.length - 1].file_version_id : "")
    );
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const isChange = Boolean(currentFvid) && selected !== currentFvid;
    const reasonRequired = isChange;
    const submit = async () => {
        setErr("");
        if (!selected) { setErr("Select a File version."); return; }
        if (reasonRequired && !reason.trim()) {
            setErr("A reason is required when changing the current Final / Current designation.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/ws4/deliverables/${deliverable.id}/mark-final-current`,
                { file_version_id: selected,
                    reason: reason.trim() || null },
                { withCredentials: true });
            onDone && await onDone();
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.error || e.message);
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title={`Mark Final / Current — ${deliverable.name}`}
                     onClose={onClose} testid="mark-final-current-modal">
            <div className="text-[12px] text-[#666] mb-3">
                Confirm the exact File version to be designated as the
                Final / Current version for this deliverable. Prior issued
                versions are preserved.
            </div>
            <div className="mb-3" data-testid="mfc-current-label">
                <strong>Current Final / Current:</strong>{" "}
                {currentFvid
                    ? <code className="text-[12px]">{currentFvid.slice(0, 8)}…</code>
                    : <span className="text-[#888]">None</span>}
            </div>
            <label className="block text-[12px] font-medium text-[#1D1D1B] mb-1">
                Version to designate
            </label>
            <select
                data-testid="mfc-version-select"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full border border-[#D5D2CB] rounded px-2 py-1.5 text-[13px] mb-3">
                {versions.length === 0
                    ? <option value="">— no versions attached —</option>
                    : versions.map(v => (
                        <option key={v.file_version_id} value={v.file_version_id}
                                data-testid={`mfc-version-opt-${v.file_version_id}`}>
                            v{v.version_number}
                            {v.file_version_id === currentFvid ? " (current)" : ""}
                        </option>
                    ))}
            </select>
            {reasonRequired && (
                <>
                    <label className="block text-[12px] font-medium text-[#1D1D1B] mb-1">
                        Reason for changing Final / Current (required)
                    </label>
                    <textarea
                        data-testid="mfc-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        className="w-full border border-[#D5D2CB] rounded px-2 py-1.5 text-[13px] mb-3" />
                </>
            )}
            {err && (
                <div data-testid="mfc-error"
                     className="text-[12px] text-red-700 mb-2">{err}</div>
            )}
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onClose}
                        data-testid="mfc-cancel"
                        className="px-3 py-1.5 text-[13px] border border-[#D5D2CB] rounded">
                    Cancel
                </button>
                <button type="button" onClick={submit} disabled={busy || !selected}
                        data-testid="mfc-submit"
                        className="px-3 py-1.5 text-[13px] bg-[#A8543F] text-white rounded disabled:opacity-50">
                    {busy ? "Working…" : "Mark Final / Current"}
                </button>
            </div>
        </ModalShell>
    );
}

/* Package Summary editor — six structured sections + four derived
   read-only values.  Draft / Complete state is authoritative from
   the backend response after every save. */
const SUMMARY_SECTIONS = [
    ["final_scheme_direction", "Final scheme / design direction"],
    ["key_client_decisions", "Key client decisions"],
    ["unresolved_matters", "Unresolved design / technical matters"],
    ["assumptions_limitations", "Assumptions / limitations"],
    ["items_deferred_beyond_free_scope", "Items deferred beyond Free Design scope"],
    ["potential_next_paid_services", "Potential next paid-service needs"],
];

function PackageSummaryModal({ projectId, initial, onClose, onDone }) {
    const [values, setValues] = useState(() => {
        const base = {};
        for (const [k] of SUMMARY_SECTIONS) {
            base[k] = (initial && initial[k]) || "";
        }
        return base;
    });
    const [state, setState] = useState(initial?.state || "draft");
    const [missing, setMissing] = useState(initial?.missing_sections || []);
    const [derived, setDerived] = useState({
        contracted: initial?.derived_contracted_revision_allowance ?? 0,
        used: initial?.derived_revision_rounds_used ?? 0,
        discretionary: initial?.derived_discretionary_rounds_approved ?? 0,
        finals: initial?.derived_final_deliverables ?? [],
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [saved, setSaved] = useState(false);

    const setField = (k, v) => setValues(o => ({ ...o, [k]: v }));
    const insertNoKnown = (k) => setField(k, "None / No known items");

    const save = async () => {
        setErr(""); setSaved(false); setBusy(true);
        try {
            const payload = {};
            for (const [k] of SUMMARY_SECTIONS) {
                payload[k] = (values[k] || "").trim() || null;
            }
            const r = await axios.put(
                `${API}/admin/projects/${projectId}/ws4/package-summary`,
                payload, { withCredentials: true });
            setState(r.data.state);
            setMissing(r.data.missing_sections || []);
            setDerived({
                contracted: r.data.derived_contracted_revision_allowance ?? 0,
                used: r.data.derived_revision_rounds_used ?? 0,
                discretionary: r.data.derived_discretionary_rounds_approved ?? 0,
                finals: r.data.derived_final_deliverables ?? [],
            });
            setSaved(true);
            onDone && await onDone();
        } catch (e) {
            setErr(e?.response?.data?.error || e.message);
        } finally { setBusy(false); }
    };

    return (
        <ModalShell title="Package Summary" onClose={onClose}
                     testid="package-summary-modal" wide>
            <div className="mb-3 flex items-center gap-2">
                <strong>State:</strong>
                <Pill tone={state === "complete" ? "good" : "warn"}
                        testid="pkg-summary-state">
                    {state}
                </Pill>
                {saved && (
                    <span data-testid="pkg-summary-saved"
                             className="text-[12px] text-[#345E34]">Saved.</span>
                )}
            </div>
            <div className="mb-4">
                <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-2">
                    Derived values (read-only)
                </h4>
                <div className="grid grid-cols-2 gap-y-1 text-[13px]"
                        data-testid="pkg-summary-derived">
                    <div>Contracted revision allowance:</div>
                    <div data-testid="pkg-derived-contracted">
                        <strong>{derived.contracted}</strong></div>
                    <div>Revision rounds used:</div>
                    <div data-testid="pkg-derived-used">
                        <strong>{derived.used}</strong></div>
                    <div>Discretionary rounds approved:</div>
                    <div data-testid="pkg-derived-discretionary">
                        <strong>{derived.discretionary}</strong></div>
                    <div>Final / Current deliverables:</div>
                    <div data-testid="pkg-derived-finals">
                        {derived.finals.length === 0
                            ? <span className="text-[#888]">None</span>
                            : <ul className="list-disc pl-5 mb-0">
                                {derived.finals.map(d => (
                                    <li key={d.deliverable_id}>
                                        {d.name}
                                    </li>
                                ))}
                            </ul>}
                    </div>
                </div>
            </div>
            <div className="space-y-3">
                {SUMMARY_SECTIONS.map(([k, label]) => {
                    const isMissing = missing.includes(k);
                    return (
                        <div key={k} data-testid={`pkg-section-${k}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <label className="text-[12px] font-medium text-[#1D1D1B]">
                                    {label}
                                </label>
                                {isMissing && (
                                    <Pill tone="warn"
                                             testid={`pkg-section-missing-${k}`}>
                                        Missing
                                    </Pill>
                                )}
                                <button type="button"
                                        data-testid={`pkg-section-none-${k}`}
                                        onClick={() => insertNoKnown(k)}
                                        className="ml-auto text-[11px] text-[#A8543F] underline">
                                    None / No known items
                                </button>
                            </div>
                            <textarea
                                data-testid={`pkg-section-input-${k}`}
                                value={values[k]}
                                onChange={(e) => setField(k, e.target.value)}
                                rows={2}
                                className="w-full border border-[#D5D2CB] rounded px-2 py-1.5 text-[13px]" />
                        </div>
                    );
                })}
            </div>
            {err && (
                <div data-testid="pkg-summary-error"
                     className="text-[12px] text-red-700 mt-3">{err}</div>
            )}
            <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={onClose}
                        data-testid="pkg-summary-cancel"
                        className="px-3 py-1.5 text-[13px] border border-[#D5D2CB] rounded">
                    Cancel
                </button>
                <button type="button" onClick={save} disabled={busy}
                        data-testid="pkg-summary-save"
                        className="px-3 py-1.5 text-[13px] bg-[#A8543F] text-white rounded disabled:opacity-50">
                    {busy ? "Saving…" : "Save Package Summary"}
                </button>
            </div>
        </ModalShell>
    );
}

export function WS4ActiveModules({ projectId, mode = "active", onCompleted }) {
    const [data, setData] = useState(null);
    const [completion, setCompletion] = useState(null);
    const [err, setErr] = useState("");
    const [mfcDeliv, setMfcDeliv] = useState(null);
    const [pkgOpen, setPkgOpen] = useState(false);
    const [creatingDI, setCreatingDI] = useState(false);
    const [editingDIDue, setEditingDIDue] = useState(null);
    const [issuingDI, setIssuingDI] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws4`,
                { withCredentials: true });
            setData(r.data);
            const c = await axios.get(
                `${API}/admin/projects/${projectId}/ws4/completion`,
                { withCredentials: true });
            setCompletion(c.data);
        } catch (e) {
            setErr(e?.response?.data?.error || e.message);
        }
    }, [projectId]);

    useEffect(() => { refresh(); }, [refresh]);

    if (err) {
        return <div data-testid="ws4-error" className="text-red-700 text-sm">{String(err)}</div>;
    }
    if (!data) {
        return <div data-testid="ws4-loading" className="text-sm text-[#888]">Loading…</div>;
    }

    const rev = data.revision_allowance || {};
    const reqs = data.package_requirements || [];
    const issues = data.design_issues || [];
    const feedbackSets = data.feedback_sets || [];
    const scope = data.scope_control_items || [];
    const summary = data.package_summary || null;
    const portal = data.portal_workspace || null;

    // D5 Slice C — coherent mode propagation via fieldset gate.
    const readOnly = mode !== "active";
    return (
        <>
        <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0"
                  data-testid={`ws4-fieldset-mode-${mode}`}>
        {readOnly && (
            <div className="mb-3 px-3 py-2 rounded-md bg-[#F5F1EA] border border-[#E5E2DC] text-[12px] text-[#666]"
                 data-testid={`ws4-mode-banner-${mode}`}>
                {mode === "future"
                    ? "This Workstage is not yet active. Content is shown for reference only."
                    : "This Workstage is completed. Historical data shown, read-only."}
            </div>
        )}
        <div className="flex flex-col gap-4" data-testid="ws4-active-modules">
            <ModuleCard n={1} title="Workstage To-Do / Progress" testid="ws4-module-1-todo">
                <div className="text-[13px] text-[#4a4740] leading-relaxed">
                    Operational To-Dos (Activate Portal · Confirm deliverables · Start design ·
                    Issue initial package · Consolidate feedback · Complete revision rounds ·
                    Issue final package · Complete Package Summary · Set Next Action) are
                    instantiated from the shared A5 / B3 Workstage Task template. This
                    listing shows a summary; live editing lives in the Internal Tasks module.
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="ws4-todo-summary">
                    <div>Contracted revision rounds: <strong data-testid="ws4-todo-contracted">{rev.contracted ?? 0}</strong></div>
                    <div>Rounds used: <strong data-testid="ws4-todo-used">{rev.used ?? 0}</strong></div>
                    <div>Discretionary approved: <strong>{rev.discretionary_approved ?? 0}</strong></div>
                    <div>Remaining: <strong>{rev.remaining ?? 0}</strong></div>
                </div>
            </ModuleCard>

            <ModuleCard n={2} title="Deliverables & Design Issues" testid="ws4-module-2-deliverables">
                {reqs.length === 0
                    ? <Empty label="No deliverables configured yet." />
                    : (
                        <table className="w-full text-[13px]" data-testid="ws4-deliverables-table">
                            <thead className="text-left text-[#666] uppercase tracking-wide text-[11px]">
                                <tr>
                                    <th className="py-1">Name</th>
                                    <th>Category</th>
                                    <th>Scope</th>
                                    <th>Required</th>
                                    <th>Status</th>
                                    <th>Current</th>
                                    <th>Final / Current</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {reqs.map(r => {
                                    const finalFv = r.final_current_file_version_id;
                                    const finalVer = (r.versions || []).find(
                                        v => v.file_version_id === finalFv);
                                    return (
                                    <tr key={r.id} className="border-t border-[#F0EDE7]"
                                        data-testid={`ws4-deliverable-row-${r.id}`}>
                                        <td className="py-1.5">{r.name}</td>
                                        <td>{(r.category || "").replace(/_/g, " ")}</td>
                                        <td>{r.required_for_package_completion ? "Required" : "Optional"}</td>
                                        <td>{r.required_for_package_completion ? "Yes" : "No"}</td>
                                        <td>
                                            <Pill tone={r.status === "final_current" ? "good"
                                                : r.status === "issued" ? "neutral" : "neutral"}
                                                testid={`ws4-deliverable-status-${r.id}`}>
                                                {r.status.replace(/_/g, " ")}
                                            </Pill>
                                        </td>
                                        <td>{r.current_version ? `v${r.current_version.version_number}` : "—"}</td>
                                        <td data-testid={`ws4-deliverable-final-${r.id}`}>
                                            {r.status === "final_current" && finalVer
                                                ? <span>v{finalVer.version_number}</span>
                                                : r.status === "final_current"
                                                    ? <span className="text-[#7A4A22]">designated</span>
                                                    : <span className="text-[#888]">—</span>}
                                        </td>
                                        <td className="text-right">
                                            <button type="button"
                                                onClick={() => setMfcDeliv(r)}
                                                disabled={(r.versions || []).length === 0}
                                                data-testid={`ws4-deliverable-mark-final-${r.id}`}
                                                className="text-[12px] text-[#A8543F] underline disabled:opacity-40 disabled:no-underline">
                                                Mark Final / Current
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                <div className="mt-4">
                    <div className="flex items-center justify-between">
                        <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide">Design Issues</h4>
                        <button
                            onClick={() => setCreatingDI(true)}
                            data-testid="ws4-design-issue-new"
                            className="text-[12px] text-[#A8543F] underline">
                            + New design issue
                        </button>
                    </div>
                    {issues.length === 0
                        ? <Empty label="No formal design issues yet." />
                        : (
                            <ul className="mt-2 space-y-1 text-[13px]" data-testid="ws4-design-issues-list">
                                {issues.map(i => (
                                    <li key={i.id}
                                        className="flex items-center gap-2"
                                        data-testid={`ws4-design-issue-row-${i.id}`}>
                                        <Pill tone={i.state === "issued" ? "good" : "neutral"}>
                                            {i.state}
                                        </Pill>
                                        <strong>{i.title}</strong>
                                        <span className="text-[#888]">
                                            · revision round {i.revision_round}
                                            · {i.file_version_snapshot?.length ?? 0} version(s)
                                            · {(i.delivery_mode || "").replace(/_/g, " ")}
                                            {i.feedback_due_date
                                                ? <> · feedback due <span data-testid={`ws4-design-issue-due-${i.id}`}>{String(i.feedback_due_date).slice(0, 10)}</span></>
                                                : null}
                                        </span>
                                        <div className="ml-auto flex items-center gap-2">
                                            {i.state === "draft" && (
                                                <button
                                                    onClick={() => setIssuingDI(i)}
                                                    data-testid={`ws4-design-issue-issue-${i.id}`}
                                                    className="text-[12px] text-[#A8543F] underline">
                                                    Issue
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setEditingDIDue(i)}
                                                data-testid={`ws4-design-issue-edit-due-${i.id}`}
                                                className="text-[12px] text-[#A8543F] underline">
                                                Edit deadline
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                </div>
            </ModuleCard>

            <ModuleCard n={3} title="Revision & Client Feedback" testid="ws4-module-3-feedback">
                <div className="mb-3 text-[13px]" data-testid="ws4-revision-panel">
                    <strong>Revision allowance:</strong>{" "}
                    contracted <strong>{rev.contracted ?? 0}</strong>,{" "}
                    used <strong>{rev.used ?? 0}</strong>,{" "}
                    discretionary <strong>{rev.discretionary_approved ?? 0}</strong>,{" "}
                    remaining <strong>{rev.remaining ?? 0}</strong>.
                </div>
                {feedbackSets.length === 0
                    ? <Empty label="No feedback sets submitted yet." />
                    : (
                        <ul className="space-y-2" data-testid="ws4-feedback-sets-list">
                            {feedbackSets.map(fs => (
                                <li key={fs.id}
                                    className="rounded border border-[#EAE7E0] p-2 text-[13px]"
                                    data-testid={`ws4-feedback-set-row-${fs.id}`}>
                                    <div className="flex items-center gap-2">
                                        <Pill tone={fs.state === "submitted" ? "warn"
                                            : fs.state === "resolved" ? "good" : "neutral"}
                                            testid={`ws4-feedback-set-state-${fs.id}`}>
                                            {fs.state}
                                        </Pill>
                                        <span>Round position {fs.revision_round_position} · {fs.item_count} item(s)</span>
                                        <a href={`/admin/projects/${projectId}/ws4/feedback-sets/${fs.id}`}
                                             className="ml-auto text-[#A8543F] underline"
                                             data-testid={`ws4-feedback-set-open-${fs.id}`}>
                                            Open review
                                        </a>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
            </ModuleCard>

            <ModuleCard n={4} title="Programme / Milestones" testid="ws4-module-4-programme">
                <div className="text-[13px] text-[#4a4740]">
                    WS4 uses the shared Project Programme. Typical WS4 items:
                    Design commenced · Initial design issue · Client feedback due ·
                    Revision Round(s) · Final Free Design Package issue.
                </div>
            </ModuleCard>

            <ModuleCard n={5} title="Client Portal & Scope Control" testid="ws4-module-5-portal-scope">
                <PortalPanel pid={projectId} portal={portal} reload={refresh}
                              clientName={data.project?.full_name
                                            || data.project?.client_name
                                            || ""} />
                <div className="mt-5">
                    <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide">Scope Control</h4>
                    {scope.length === 0
                        ? <Empty label="No scope-control classifications recorded." />
                        : (
                            <ul className="mt-2 space-y-1 text-[13px]" data-testid="ws4-scope-control-list">
                                {scope.map(s => (
                                    <li key={s.id}
                                        className="flex items-start gap-2"
                                        data-testid={`ws4-scope-row-${s.id}`}>
                                        <Pill tone={s.classification === "proposed_paid_additional" ? "warn" : "neutral"}>
                                            {(s.classification || "").replace(/_/g, " ")}
                                        </Pill>
                                        <span>
                                            <strong>{s.request_text}</strong>
                                            <div className="text-[12px] text-[#666]">Reason: {s.reason}</div>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                </div>
            </ModuleCard>

            <ModuleCard n={6} title="Free Design Package Completion" testid="ws4-module-6-completion">
                <div className="mb-3 flex items-center gap-2" data-testid="ws4-package-summary-panel">
                    <strong>Package Summary:</strong>{" "}
                    <Pill tone={summary?.state === "complete" ? "good" : "warn"}
                             testid="ws4-package-summary-state">
                        {summary?.state || "not started"}
                    </Pill>
                    <button type="button"
                        onClick={() => setPkgOpen(true)}
                        data-testid="ws4-package-summary-edit"
                        className="ml-auto text-[12px] text-[#A8543F] underline">
                        View / Edit Package Summary
                    </button>
                </div>
                {summary?.state === "complete" && summary.final_scheme_direction && (
                    <div className="mb-3 text-[13px] text-[#4a4740]"
                            data-testid="ws4-package-summary-preview">
                        <strong>Direction:</strong> {summary.final_scheme_direction}
                    </div>
                )}
                <div className="mt-2" data-testid="ws4-completion-blockers-panel">
                    <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide">
                        Completion blockers
                    </h4>
                    {completion?.can_complete
                        ? <div className="mt-1 text-[13px] text-[#345E34]" data-testid="ws4-can-complete">
                            All completion facts satisfied. WS4 → WS5 transition available.
                        </div>
                        : (
                            <ul className="mt-1 list-disc pl-5 text-[13px] text-[#7A5148]"
                                 data-testid="ws4-blockers-list">
                                {(completion?.blockers || []).map((b, i) => (
                                    <li key={i} data-testid={`ws4-blocker-${b.code}`}>{b.message}</li>
                                ))}
                            </ul>
                        )}
                </div>
            </ModuleCard>
        </div>
        </fieldset>
        {mfcDeliv && (
            <MarkFinalCurrentModal
                projectId={projectId}
                deliverable={mfcDeliv}
                onClose={() => setMfcDeliv(null)}
                onDone={refresh} />
        )}
        {pkgOpen && (
            <PackageSummaryModal
                projectId={projectId}
                initial={summary}
                onClose={() => setPkgOpen(false)}
                onDone={refresh} />
        )}
        {creatingDI && (
            <DesignIssueCreateModal
                projectId={projectId}
                onClose={() => setCreatingDI(false)}
                onDone={() => { setCreatingDI(false); refresh(); }} />
        )}
        {editingDIDue && (
            <DesignIssueEditDueModal
                projectId={projectId}
                issue={editingDIDue}
                onClose={() => setEditingDIDue(null)}
                onDone={() => { setEditingDIDue(null); refresh(); }} />
        )}
        {issuingDI && (
            <DesignIssueIssueModal
                projectId={projectId}
                issue={issuingDI}
                onClose={() => setIssuingDI(null)}
                onDone={() => { setIssuingDI(null); refresh(); }} />
        )}
        </>
    );
}

export default WS4ActiveModules;


function DesignIssueCreateModal({ projectId, onClose, onDone }) {
    const [title, setTitle] = useState("");
    const [note, setNote] = useState("");
    const [feedbackDue, setFeedbackDue] = useState("");
    const [mode, setMode] = useState("portal_only");
    const [versionIdsRaw, setVersionIdsRaw] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async () => {
        setBusy(true); setErr("");
        try {
            const ids = versionIdsRaw
                .split(",").map(s => s.trim()).filter(Boolean);
            if (!title.trim()) throw new Error("Title required");
            if (ids.length === 0) throw new Error("At least one file_version_id required");
            await axios.post(
                `${API}/admin/projects/${projectId}/ws4/design-issues`,
                {
                    title: title.trim(),
                    file_version_ids: ids,
                    client_facing_note: note || null,
                    delivery_mode: mode,
                    feedback_due_date: feedbackDue || null,
                    revision_round: 0,
                    is_final_package: false,
                    create_op_id: `di-${Date.now()}`,
                },
                { withCredentials: true },
            );
            onDone();
        } catch (e) {
            const d = e?.response?.data?.detail;
            const msg = typeof d === "string" ? d
                : (d?.error || d?.message)
                || e?.response?.data?.error
                || e.message || "Failed";
            setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title="New Design Issue" onClose={onClose}
            testid="ws4-design-issue-create-modal">
            <label className="flex flex-col gap-1">
                <span className="text-[11px] uppercase text-[#666]">Title</span>
                <input value={title} onChange={e => setTitle(e.target.value)}
                    data-testid="ws4-di-title"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            <label className="flex flex-col gap-1">
                <span className="text-[11px] uppercase text-[#666]">
                    File version IDs (comma-separated, from Project Files)
                </span>
                <input value={versionIdsRaw}
                    onChange={e => setVersionIdsRaw(e.target.value)}
                    data-testid="ws4-di-file-version-ids"
                    placeholder="fv_abc, fv_def"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            <label className="flex flex-col gap-1">
                <span className="text-[11px] uppercase text-[#666]">Delivery mode</span>
                <select value={mode} onChange={e => setMode(e.target.value)}
                    data-testid="ws4-di-delivery-mode"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]">
                    <option value="portal_only">portal_only</option>
                    <option value="email_attachments_and_portal">email_attachments_and_portal</option>
                    <option value="secure_download_link_and_portal">secure_download_link_and_portal</option>
                </select>
            </label>
            <label className="flex flex-col gap-1">
                <span className="text-[11px] uppercase text-[#666]">
                    Client feedback due date (optional)
                </span>
                <input type="date" value={feedbackDue}
                    onChange={e => setFeedbackDue(e.target.value)}
                    data-testid="ws4-di-feedback-due-date"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            <label className="flex flex-col gap-1">
                <span className="text-[11px] uppercase text-[#666]">Client-facing note (optional)</span>
                <textarea rows={3} value={note}
                    onChange={e => setNote(e.target.value)}
                    data-testid="ws4-di-client-note"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            {err && <div className="text-[12px] text-terracotta" data-testid="ws4-di-create-error">{err}</div>}
            <div className="flex justify-end gap-2">
                <button onClick={onClose} disabled={busy}
                    className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">Cancel</button>
                <button onClick={submit} disabled={busy}
                    data-testid="ws4-di-create-submit"
                    className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                    Save Draft
                </button>
            </div>
        </ModalShell>
    );
}


function DesignIssueEditDueModal({ projectId, issue, onClose, onDone }) {
    const [feedbackDue, setFeedbackDue] = useState(
        issue.feedback_due_date ? String(issue.feedback_due_date).slice(0, 10) : "",
    );
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async () => {
        setBusy(true); setErr("");
        try {
            await axios.patch(
                `${API}/admin/projects/${projectId}/ws4/design-issues/${issue.id}/feedback-due-date`,
                {
                    feedback_due_date: feedbackDue || null,
                    op_id: `di-due-${issue.id}-${Date.now()}`,
                },
                { withCredentials: true },
            );
            onDone();
        } catch (e) {
            setErr(readErrorDetail(e, "Failed"));
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title={`Revise feedback deadline · ${issue.title}`}
            onClose={onClose}
            testid="ws4-design-issue-edit-due-modal">
            <label className="flex flex-col gap-1">
                <span className="text-[11px] uppercase text-[#666]">
                    New client feedback due date
                </span>
                <input type="date" value={feedbackDue}
                    onChange={e => setFeedbackDue(e.target.value)}
                    data-testid="ws4-di-edit-due-input"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
            </label>
            {err && <div className="text-[12px] text-terracotta">{err}</div>}
            <div className="flex justify-end gap-2">
                <button onClick={onClose} disabled={busy}
                    className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">Cancel</button>
                <button onClick={submit} disabled={busy}
                    data-testid="ws4-di-edit-due-submit"
                    className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                    Save
                </button>
            </div>
        </ModalShell>
    );
}


function DesignIssueIssueModal({ projectId, issue, onClose, onDone }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async () => {
        setBusy(true); setErr("");
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/ws4/design-issues/${issue.id}/issue`,
                { create_op_id: `di-issue-${issue.id}-${Date.now()}` },
                { withCredentials: true },
            );
            onDone();
        } catch (e) {
            setErr(e?.response?.data?.detail?.error
                    || e?.response?.data?.detail
                    || e.message || "Failed");
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title={`Formally issue Design Issue · ${issue.title}`}
            onClose={onClose}
            testid="ws4-design-issue-issue-modal">
            <div className="text-[13px] text-[#4a4740]">
                Delivery mode: <strong>{(issue.delivery_mode || "").replace(/_/g, " ")}</strong>.
                Client feedback due date:{" "}
                <strong>{issue.feedback_due_date || "—"}</strong>.
                {issue.file_version_snapshot?.length ?? 0} version(s) attached.
            </div>
            <div className="text-[12px] text-[#888]">
                This will publish the exact file versions and mark the Design Issue as issued.
            </div>
            {err && <div className="text-[12px] text-terracotta">{err}</div>}
            <div className="flex justify-end gap-2">
                <button onClick={onClose} disabled={busy}
                    className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">Cancel</button>
                <button onClick={submit} disabled={busy}
                    data-testid="ws4-di-issue-submit"
                    className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                    Confirm Issue
                </button>
            </div>
        </ModalShell>
    );
}


// ============================================================
// 5410-01 — Admin Feedback Review page (route: /admin/projects/:pid/ws4/feedback-sets/:fsid)
// Full-width vertical modules per §16 of the B7a brief.
// ============================================================

const ITEM_ACTIONS = [
    { key: "mark_in_review", label: "Mark In Review" },
    { key: "add_to_revision", label: "Add to Revision" },
    { key: "resolve", label: "Resolve" },
    { key: "defer", label: "Defer" },
    { key: "outside_scope", label: "Outside Scope" },
    { key: "not_applicable", label: "Not Applicable" },
];

function FeedbackReviewPage() {
    // Read the route params from window.location so this component
    // is drop-in whatever routing library is in use.
    const m = window.location.pathname.match(
        /\/admin\/projects\/([^/]+)\/ws4\/feedback-sets\/([^/]+)$/);
    const pid = m ? m[1] : "";
    const fsid = m ? m[2] : "";
    const [set, setSet] = useState(null);
    const [items, setItems] = useState([]);
    const [reason, setReason] = useState({});
    const [note, setNote] = useState({});
    const [selected, setSelected] = useState({});
    const [bulkChoice, setBulkChoice] = useState("mark_in_review");
    const [bulkReason, setBulkReason] = useState("");
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);
    const setLabel = set?.reference || set?.title || "Feedback set";
    usePageMeta({ title: `${setLabel} · WS4 Feedback | Studio G4 Admin` });

    const load = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${pid}/ws4/feedback-sets/${fsid}`,
                { withCredentials: true });
            setSet(r.data.set);
            setItems(r.data.items || []);
        } catch (e) {
            setMsg(`Load failed: ${e?.response?.data?.detail?.error || e.message}`);
        }
    }, [pid, fsid]);

    useEffect(() => { if (pid && fsid) load(); }, [pid, fsid, load]);

    async function itemAction(id, actionKey) {
        setBusy(true);
        try {
            const body = {};
            if (actionKey === "outside_scope") {
                if (!(reason[id] || "").trim()) {
                    setMsg("Outside Scope requires a reason.");
                    setBusy(false); return;
                }
                body.state = "outside_scope";
                body.outside_scope_reason = reason[id];
            } else if (actionKey === "defer") {
                if (!(reason[id] || "").trim()) {
                    setMsg("Deferred requires a reason.");
                    setBusy(false); return;
                }
                body.state = "deferred";
                body.deferred_reason = reason[id];
            } else if (actionKey === "resolve") {
                body.state = "resolved";
                if (note[id]) body.admin_resolution_note = note[id];
            } else if (actionKey === "mark_in_review") {
                body.state = "in_review";
            } else if (actionKey === "not_applicable") {
                body.state = "not_applicable";
            } else if (actionKey === "add_to_revision") {
                body.revision_round = (set?.revision_round_position || 0) + 1;
            }
            await axios.patch(
                `${API}/admin/projects/${pid}/ws4/feedback-items/${id}`,
                body, { withCredentials: true });
            setMsg(`Item updated: ${actionKey}`);
            await load();
        } catch (e) {
            setMsg(`Action failed: ${e?.response?.data?.detail?.error || e.message}`);
        } finally { setBusy(false); }
    }

    async function bulkAction() {
        const ids = Object.keys(selected).filter(k => selected[k]);
        if (!ids.length) { setMsg("Select at least one item"); return; }
        if (bulkChoice === "defer" && !bulkReason.trim()) {
            setMsg("Bulk Defer requires a reason"); return;
        }
        setBusy(true);
        try {
            await axios.post(
                `${API}/admin/projects/${pid}/ws4/feedback-items/bulk`,
                { ids, action: bulkChoice,
                  reason: bulkReason || undefined,
                  create_op_id: `ui-bulk-${Date.now()}` },
                { withCredentials: true });
            setMsg(`Bulk ${bulkChoice} applied to ${ids.length} item(s)`);
            setSelected({});
            setBulkReason("");
            await load();
        } catch (e) {
            setMsg(`Bulk failed: ${e?.response?.data?.detail?.error || e.message}`);
        } finally { setBusy(false); }
    }

    async function startRevision() {
        const ids = Object.keys(selected).filter(k => selected[k]);
        if (!ids.length) { setMsg("Select at least one item"); return; }
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/admin/projects/${pid}/ws4/revisions/start`,
                { feedback_item_ids: ids,
                  create_op_id: `ui-rev-${Date.now()}` },
                { withCredentials: true });
            setMsg(`Revision round ${r.data.revision_round} started`);
            setSelected({});
            await load();
        } catch (e) {
            setMsg(`Start revision failed: ${e?.response?.data?.detail?.error || e.message}`);
        } finally { setBusy(false); }
    }

    async function resolveSet() {
        setBusy(true);
        try {
            await axios.post(
                `${API}/admin/projects/${pid}/ws4/feedback-sets/${fsid}/resolve`,
                { create_op_id: `ui-resolve-${Date.now()}` },
                { withCredentials: true });
            setMsg("Feedback Set resolved");
            await load();
        } catch (e) {
            setMsg(`Resolve failed: ${e?.response?.data?.detail?.error || e.message}`);
        } finally { setBusy(false); }
    }

    async function reopenSet() {
        const r = window.prompt("Reason to reopen?");
        if (!r || !r.trim()) return;
        setBusy(true);
        try {
            await axios.post(
                `${API}/admin/projects/${pid}/ws4/feedback-sets/${fsid}/reopen`,
                { reason: r, create_op_id: `ui-reopen-${Date.now()}` }, { withCredentials: true });
            setMsg("Feedback Set reopened");
            await load();
        } catch (e) {
            setMsg(`Reopen failed: ${e?.response?.data?.detail?.error || e.message}`);
        } finally { setBusy(false); }
    }

    if (!set) return <div className="p-6" data-testid="feedback-review-loading">Loading feedback set…</div>;

    const terminal = new Set(["resolved", "not_applicable", "deferred", "outside_scope"]);
    const openCount = items.filter(i => !terminal.has(i.state)).length;
    const summary = {
        total: items.length,
        open: items.filter(i => i.state === "open").length,
        in_review: items.filter(i => i.state === "in_review").length,
        resolved: items.filter(i => i.state === "resolved").length,
        deferred: items.filter(i => i.state === "deferred").length,
        outside_scope: items.filter(i => i.state === "outside_scope").length,
        not_applicable: items.filter(i => i.state === "not_applicable").length,
    };

    return (
        <div className="mx-auto max-w-5xl space-y-4 p-6" data-testid="feedback-review-page">
            <a href={`/admin/projects/${pid}`} className="text-[13px] text-[#7A6A50] underline"
                 data-testid="feedback-review-back">← Back to workspace</a>
            <h1 className="text-2xl font-semibold text-[#1D1D1B]">Feedback Review</h1>

            <ModuleCard n={1} title="Feedback Set Summary" testid="fbr-module-1-summary">
                <dl className="grid grid-cols-2 gap-2 text-[13px]">
                    <div><dt className="text-[#7A6A50]">State</dt><dd data-testid="fbr-set-state">{set.state}</dd></div>
                    <div><dt className="text-[#7A6A50]">Design Issue</dt><dd>{set.design_issue_id}</dd></div>
                    <div><dt className="text-[#7A6A50]">Submitted</dt><dd>{set.submitted_at || "—"}</dd></div>
                    <div><dt className="text-[#7A6A50]">Round position</dt><dd>{set.revision_round_position ?? 0}</dd></div>
                    <div><dt className="text-[#7A6A50]">Items</dt><dd>{summary.total}</dd></div>
                    <div><dt className="text-[#7A6A50]">Open / In review</dt><dd>{summary.open} / {summary.in_review}</dd></div>
                </dl>
            </ModuleCard>

            <ModuleCard n={2} title="Feedback Items Review" testid="fbr-module-2-items">
                {items.length === 0 ? <Empty label="No items" /> : (
                    <ul className="space-y-3">
                        {items.map(item => (
                            <li key={item.id}
                                 data-testid={`fbr-item-${item.id}`}
                                 className="rounded border border-[#E5E2DC] p-3">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <input type="checkbox"
                                             className="mr-2"
                                             data-testid={`fbr-item-select-${item.id}`}
                                             checked={!!selected[item.id]}
                                             onChange={(e) => setSelected(s => ({...s, [item.id]: e.target.checked}))} />
                                        <strong data-testid={`fbr-item-subject-${item.id}`}>{item.subject}</strong>
                                        <span className="ml-2 text-[12px] text-[#7A6A50]">
                                            state: <span data-testid={`fbr-item-state-${item.id}`}>{item.state}</span>
                                        </span>
                                    </div>
                                </div>
                                <p className="mt-2 whitespace-pre-line text-[13px]"
                                     data-testid={`fbr-item-comment-${item.id}`}>{item.client_comment}</p>
                                {item.resolving_design_issue_id && (
                                    <p className="mt-2 text-[12px] text-[#345E34]"
                                         data-testid={`fbr-item-resolving-${item.id}`}>
                                        Resolving Design Issue: {item.resolving_design_issue_id}
                                    </p>
                                )}
                                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    <input
                                        placeholder="Reason (Outside Scope / Defer)"
                                        value={reason[item.id] || ""}
                                        onChange={(e) => setReason(r => ({...r, [item.id]: e.target.value}))}
                                        data-testid={`fbr-item-reason-${item.id}`}
                                        className="rounded border border-[#E5E2DC] px-2 py-1 text-[13px]" />
                                    <input
                                        placeholder="Resolution note (optional)"
                                        value={note[item.id] || ""}
                                        onChange={(e) => setNote(n => ({...n, [item.id]: e.target.value}))}
                                        data-testid={`fbr-item-note-${item.id}`}
                                        className="rounded border border-[#E5E2DC] px-2 py-1 text-[13px]" />
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {ITEM_ACTIONS.map(a => (
                                        <button key={a.key}
                                             disabled={busy || terminal.has(item.state)}
                                             onClick={() => itemAction(item.id, a.key)}
                                             data-testid={`fbr-item-action-${a.key}-${item.id}`}
                                             className="rounded border border-[#E5E2DC] bg-white px-2 py-1 text-[12px] hover:bg-[#F5F3EE] disabled:opacity-50">
                                            {a.label}
                                        </button>
                                    ))}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </ModuleCard>

            <ModuleCard n={3} title="Revision Round Control" testid="fbr-module-3-revision">
                <p className="text-[13px] text-[#7A6A50]">
                    Select item(s) above and start a revision round.  Only a deliberate Start consumes an allowance round.
                </p>
                <button onClick={startRevision} disabled={busy}
                     data-testid="fbr-start-revision"
                     className="mt-2 rounded bg-[#A8543F] px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50">
                    Start Revision Round from selection
                </button>
            </ModuleCard>

            <ModuleCard n={4} title="Bulk Actions" testid="fbr-module-4-bulk">
                <div className="flex flex-wrap items-end gap-2">
                    <select value={bulkChoice} onChange={(e) => setBulkChoice(e.target.value)}
                         data-testid="fbr-bulk-action"
                         className="rounded border border-[#E5E2DC] px-2 py-1 text-[13px]">
                        <option value="mark_in_review">Mark In Review</option>
                        <option value="add_to_revision">Add to Revision Round</option>
                        <option value="mark_resolved">Mark Resolved</option>
                        <option value="defer">Defer (reason required)</option>
                    </select>
                    <input placeholder="Reason (for Defer)"
                         value={bulkReason}
                         onChange={(e) => setBulkReason(e.target.value)}
                         data-testid="fbr-bulk-reason"
                         className="rounded border border-[#E5E2DC] px-2 py-1 text-[13px]" />
                    <button onClick={bulkAction} disabled={busy}
                         data-testid="fbr-bulk-apply"
                         className="rounded bg-[#1D1D1B] px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50">
                        Apply
                    </button>
                    <span className="text-[12px] text-[#7A6A50]" data-testid="fbr-bulk-note">
                        (Outside Scope is intentionally not a bulk action.)
                    </span>
                </div>
            </ModuleCard>

            <ModuleCard n={5} title="Feedback Set Review Summary" testid="fbr-module-5-review-summary">
                <ul className="text-[13px]">
                    <li>Open: <span data-testid="fbr-summary-open">{summary.open}</span></li>
                    <li>In review: <span data-testid="fbr-summary-in-review">{summary.in_review}</span></li>
                    <li>Resolved: {summary.resolved}</li>
                    <li>Deferred: {summary.deferred}</li>
                    <li>Outside scope: {summary.outside_scope}</li>
                    <li>Not applicable: {summary.not_applicable}</li>
                </ul>
            </ModuleCard>

            <ModuleCard n={6} title="Resolve Feedback Set" testid="fbr-module-6-resolve">
                {openCount > 0 && (
                    <p className="text-[13px] text-[#A54A2A]" data-testid="fbr-resolve-blocker">
                        Resolve is blocked: {openCount} item(s) still Open / In Review.
                    </p>
                )}
                <div className="mt-2 flex gap-2">
                    <button onClick={resolveSet} disabled={busy || openCount > 0}
                         data-testid="fbr-resolve"
                         className="rounded bg-[#345E34] px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50">
                        Resolve Feedback Set
                    </button>
                    <button onClick={reopenSet} disabled={busy || set.state !== "resolved"}
                         data-testid="fbr-reopen"
                         className="rounded border border-[#E5E2DC] bg-white px-3 py-1.5 text-[13px] font-medium disabled:opacity-50">
                        Reopen (reason required)
                    </button>
                </div>
            </ModuleCard>

            <ModuleCard n={7} title="Activity / History" testid="fbr-module-7-history">
                <p className="text-[13px] text-[#7A6A50]">
                    All material events are captured in the Project Activity stream.
                    Item comments and attachments are immutable.
                </p>
            </ModuleCard>

            {msg && <div data-testid="fbr-msg"
                            className="rounded border border-[#E5E2DC] bg-[#F5F3EE] px-3 py-2 text-[13px]">{msg}</div>}
        </div>
    );
}

export { FeedbackReviewPage };
